import React, { Component } from 'react';
import { Button, Form, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ArrowBackOutlinedIcon from '@material-ui/icons/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@material-ui/icons/ArrowForwardOutlined';
import MicOutlinedIcon from '@material-ui/icons/MicOutlined';
import GraphicEqIcon from '@material-ui/icons/GraphicEq';
import Add from '@material-ui/icons/Add';

import axios from '../../../../axios';
import apiError from '../../../../error';
import { getCookie, isTruthyValue } from '../../../../function';
import Spinner from '../../../spinner/spinner';
import '../../../create-template/voice-selection/voice-selection.css';
import SelectVoiceModal from '../../../create-template/voice-selection/select-voice-modal';
import MiniVoicePreview from '../../../create-template/voice-selection/mini-voice-preview';
import AddNewVoiceModal from '../../../settings/deepfake/add-new-voice-modal';

export default class VoiceSelection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            voiceMode: 'default',
            selectedVoice: null,
            showSelectVoiceModal: false,
            voices: [],
            listPreviewPlayerId: null,
            showAddNewVoiceModal: false,
            loading: false,
        }
    }

    componentDidMount() {
        this.loadSelectedVoices();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.vishingVoiceSelectionId !== this.props.vishingVoiceSelectionId) {
            this.loadSelectedVoices();
        }
        if (this.isVoiceDeepfakeLicensed() && prevState.voiceMode !== 'deepfake' && this.state.voiceMode === 'deepfake') {
            this.loadSelectedVoices({ syncVoiceModeFromServer: false });
        }
    }

    isVoiceDeepfakeLicensed = () => isTruthyValue(getCookie("isDeepFakeEnabled"));

    getPromptConfig = () => {
        return {
            title: "Voice Selection",
            desc: "Choose a voice for attack",
        }
    }

    // Lazily creates a vishing_voice_selection record if one doesn't exist yet
    ensureVishingVoiceSelection = async () => {
        const existingId = this.props.vishingVoiceSelectionId;
        if (existingId != null && String(existingId).trim() !== '') return existingId;

        const { campaignTemplateId } = this.props;
        if (!campaignTemplateId) return null;

        const res = await axios.post('/api/vishing-voice-selection/', {
            campaignTemplateId,
            isDeepfakeEnabled: false,
        });
        const newId = res.data.vishingVoiceSelectionId;
        if (newId && this.props.onVishingVoiceSelectionCreated) {
            this.props.onVishingVoiceSelectionCreated(newId);
        }
        return newId;
    }

    saveDeepfakePromptToServer = async (isDeepfakeEnabled, voiceOrNull, overrideId = null) => {
        const id = overrideId ?? this.props.vishingVoiceSelectionId;
        if (id == null || String(id).trim() === '') return;

        const voiceId =
            isDeepfakeEnabled && voiceOrNull
                ? voiceOrNull.voiceId ?? voiceOrNull.id
                : null;
        await axios.patch(`/api/vishing-voice-selection/${id}`, {
            isDeepfakeEnabled,
            voiceId: isDeepfakeEnabled ? voiceId : null,
        });
    }

    handleDeepfakeVoiceRowSelect = async (voice) => {
        if (this.state.voiceMode !== 'deepfake') return;

        const id = this.props.vishingVoiceSelectionId;
        const hasId = id != null && String(id).trim() !== '';

        if (hasId) {
            try {
                await this.saveDeepfakePromptToServer(true, voice);
                this.setState({ selectedVoice: voice }, () => this.loadSelectedVoices());
            } catch (err) {
                apiError(err);
            }
            return;
        }

        this.setState({ selectedVoice: voice });
    }

    loadSelectedVoices = (options = {}) => {
        const { syncVoiceModeFromServer = true } = options;
        const id = this.props.vishingVoiceSelectionId;
        if (id == null || String(id).trim() === '') {
            return;
        }
        const canUseDeepfake = this.isVoiceDeepfakeLicensed();
        const fetchUrl = `/api/vishing-voice-selection/${id}`;

        const requests = canUseDeepfake
            ? [axios.get(fetchUrl), axios.get('/api/deepfake/list-voice')]
            : [axios.get(fetchUrl), Promise.resolve({ data: { data: { voices: [] } } })];

        Promise.all(requests)
            .then(([promptRes, listRes]) => {
                const prompt =
                    promptRes.data?.vishingVoiceSelection ||
                    promptRes.data?.data?.vishingVoiceSelection ||
                    {};
                if (!canUseDeepfake) {
                    this.setState({
                        voiceMode: 'default',
                        selectedVoice: null,
                        voices: [],
                    });
                    return;
                }
                const savedVoiceIdRaw = prompt.voiceId;
                const savedVoiceId =
                    savedVoiceIdRaw != null && String(savedVoiceIdRaw).trim() !== ''
                        ? String(savedVoiceIdRaw)
                        : null;

                const arr = listRes.data?.data?.voices || listRes.data?.voices || [];
                const list = Array.isArray(arr) ? arr : [];

                const matched =
                    savedVoiceId &&
                    list.find((v) => String(v.voiceId ?? v.id ?? '') === savedVoiceId);

                const nextVoiceModeFromPrompt = prompt.isDeepfakeEnabled ? 'deepfake' : 'default';
                const modePatch = syncVoiceModeFromServer
                    ? { voiceMode: nextVoiceModeFromPrompt }
                    : {};

                this.setState((prev) => {
                    const prevSel = prev.selectedVoice;

                    if (matched) {
                        return {
                            voices: [matched],
                            selectedVoice: matched,
                            ...modePatch,
                        };
                    }

                    if (
                        savedVoiceId &&
                        prevSel &&
                        String(prevSel.voiceId ?? prevSel.id ?? '') === savedVoiceId
                    ) {
                        return {
                            voices: [prevSel],
                            selectedVoice: prevSel,
                            ...modePatch,
                        };
                    }

                    if (savedVoiceId) {
                        return {
                            voices: [],
                            selectedVoice: null,
                            ...modePatch,
                        };
                    }

                    if (prevSel) {
                        const sid = String(prevSel.voiceId ?? prevSel.id ?? '');
                        if (sid) {
                            const fromList = list.find((v) => String(v.voiceId ?? v.id ?? '') === sid);
                            const row = fromList || prevSel;
                            return {
                                voices: [row],
                                selectedVoice: row,
                                ...modePatch,
                            };
                        }
                    }

                    return {
                        voices: [],
                        selectedVoice: null,
                        ...modePatch,
                    };
                });
            })
            .catch((err) => {
                apiError(err);
            });
    }

    handleSelectVoice = () => {
        if (!this.isVoiceDeepfakeLicensed()) return;
        this.setState({ showSelectVoiceModal: true });
    }

    handleCloseSelectVoiceModal = () => {
        this.setState({ showSelectVoiceModal: false });
    }

    handleVoiceChosenFromModal = (voice) => {
        this.setState({ selectedVoice: voice }, () => this.loadSelectedVoices());
    }



    handleAddNewVoice = () => {
        if (!this.isVoiceDeepfakeLicensed()) return;
        this.setState({ showAddNewVoiceModal: true });
    }

    handleCloseAddNewVoiceModal = () => {
        this.setState({ showAddNewVoiceModal: false });
    }

    handleCreateAiVishingPrompt = async () => {
        const canUseDeepfake = this.isVoiceDeepfakeLicensed();
        if (canUseDeepfake && this.state.voiceMode === 'deepfake' && !this.state.selectedVoice) {
            toast.error('Please select a deepfake voice profile.');
            return;
        }

        this.setState({ loading: true });
        try {
            // Ensure a vishing_voice_selection record exists (creates lazily if needed for vishing)
            const resolvedId = await this.ensureVishingVoiceSelection();

            if (!canUseDeepfake || this.state.voiceMode === 'default') {
                await this.saveDeepfakePromptToServer(false, null, resolvedId);
            } else {
                await this.saveDeepfakePromptToServer(true, this.state.selectedVoice, resolvedId);
            }
            this.setState({ loading: false });
            if (this.props.onVoiceSelectionStepComplete) {
                this.props.onVoiceSelectionStepComplete();
            }
            if (this.props.handleTabChange) {
                this.props.handleTabChange('next');
            }
        } catch (err) {
            this.setState({ loading: false });
            apiError(err);
        }
    }

    render() {
        const config = this.getPromptConfig();
        const canUseDeepfake = this.isVoiceDeepfakeLicensed();
        return (
            <>
                <div className="tabContent-block email-template-wrap">
                    <div className='rightSectionHeader'>
                        <div className="row justify-content-between d-flex align-items-center">
                            <div className="col-md-6 tab-right-header">
                                <h6>{config.title}</h6>
                                <p>{config.desc}</p>
                            </div>
                            <div className="col-md-3">
                            </div>
                        </div>
                    </div>

                    <div className="row voice-selection-row">
                        <div className="col-md-6 voice-selection-col">
                            <div
                                className={`voice-selection-card ${this.state.voiceMode === 'default' ? 'voice-selection-card--active' : ''}`}
                                role="radio"
                                aria-checked={this.state.voiceMode === 'default'}
                                onClick={() => this.setState({ voiceMode: 'default' })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        this.setState({ voiceMode: 'default' });
                                    }
                                }}
                                tabIndex={0}
                            >
                                <div className="voice-selection-card__main">
                                    <div className="voice-selection-icon voice-selection-icon--default">
                                        <MicOutlinedIcon />
                                    </div>
                                    <h6 className="voice-selection-title">Default voice</h6>
                                    <p className="voice-selection-desc">Standard AI-generated voice. Clean, professional, and ready to use instantly.</p>
                                </div>
                                <Form.Check
                                    type="radio"
                                    name="voiceMode"
                                    id="voice-mode-default"
                                    className="voice-selection-radio"
                                    checked={this.state.voiceMode === 'default'}
                                    onChange={() => this.setState({ voiceMode: 'default' })}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                        <div className="col-md-6 voice-selection-col">
                            {canUseDeepfake ? (
                                <div
                                    className={`voice-selection-card ${this.state.voiceMode === 'deepfake' ? 'voice-selection-card--active' : ''}`}
                                    role="radio"
                                    aria-checked={this.state.voiceMode === 'deepfake'}
                                    onClick={() => this.setState({ voiceMode: 'deepfake' })}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            this.setState({ voiceMode: 'deepfake' });
                                        }
                                    }}
                                    tabIndex={0}
                                >
                                    <div className="voice-selection-card__main">
                                        <div className="voice-selection-icon voice-selection-icon--deepfake">
                                            <GraphicEqIcon />
                                        </div>
                                        <h6 className="voice-selection-title">Deep fake voice</h6>
                                        <p className="voice-selection-desc">Use a real human voice cloned by AI. Choose from admin-uploaded voices profiles.</p>
                                    </div>
                                    <Form.Check
                                        type="radio"
                                        name="voiceMode"
                                        id="voice-mode-deepfake"
                                        className="voice-selection-radio"
                                        checked={this.state.voiceMode === 'deepfake'}
                                        onChange={() => this.setState({ voiceMode: 'deepfake' })}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            ) : (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip id="deepfake-license-tooltip">
                                            This feature is not covered under your current license.
                                        </Tooltip>
                                    }
                                >
                                    {/* wrapper div keeps pointer-events so tooltip can fire */}
                                    <div className="voice-selection-card--disabled-wrapper">
                                        <div
                                            className="voice-selection-card voice-selection-card--disabled"
                                            role="radio"
                                            aria-checked={false}
                                            aria-disabled={true}
                                            tabIndex={-1}
                                        >
                                            <div className="voice-selection-card__main">
                                                <div className="voice-selection-icon voice-selection-icon--deepfake">
                                                    <GraphicEqIcon />
                                                </div>
                                                <h6 className="voice-selection-title">Deep fake voice</h6>
                                                <p className="voice-selection-desc">Use a real human voice cloned by AI. Choose from admin-uploaded voices profiles.</p>
                                            </div>
                                            <Form.Check
                                                type="radio"
                                                name="voiceMode"
                                                id="voice-mode-deepfake"
                                                className="voice-selection-radio"
                                                checked={false}
                                                disabled={true}
                                                onChange={() => {}}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                </OverlayTrigger>
                            )}
                        </div>
                    </div>
                    {canUseDeepfake && this.state.voiceMode !== 'default' ? (
                        <div className="row voice-selection-row voice-selection-row--panel">
                            <div className="col-12 voice-selection-deepfake-wrap">
                                <div className="voice-selection-panel">
                                    <h6 className="voice-selection-panel__heading">Choose a deepfake voice</h6>
                                    {!this.state.voices.length && !this.state.selectedVoice ? (
                                        <div className="voice-selection-empty-row">
                                            <div className="voice-selection-empty-left">
                                                <div className="voice-selection-mic-plus" aria-hidden>
                                                    <MicOutlinedIcon className="voice-selection-mic-plus__mic" />
                                                    <span className="voice-selection-mic-plus__badge">
                                                        <Add className="voice-selection-mic-plus__add" />
                                                    </span>
                                                </div>
                                                <span className="voice-selection-empty-label">No voice selected</span>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                className="voice-selection-btn-select"
                                                onClick={this.handleSelectVoice}
                                            >
                                                Select Voice
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="voice-selection-card__list">
                                            {(this.state.voices.length
                                                ? this.state.voices
                                                : this.state.selectedVoice
                                                    ? [this.state.selectedVoice]
                                                    : []
                                            ).map((voice, index) => {
                                                const id = String(voice.voiceId ?? voice.id ?? index);
                                                const sel = this.state.selectedVoice;
                                                const isActive =
                                                    sel &&
                                                    String(sel.voiceId ?? sel.id ?? '') === id;
                                                return (
                                                    <div
                                                        key={`voice-row-${id}`}
                                                        role="button"
                                                        tabIndex={0}
                                                        className={`voice-selection-card__list-item${isActive ? ' voice-selection-card__list-item--active' : ''}`}
                                                        onClick={() => this.handleDeepfakeVoiceRowSelect(voice)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                this.handleDeepfakeVoiceRowSelect(voice);
                                                            }
                                                        }}
                                                    >
                                                        <div className="voice-selection-card__list-item-left">
                                                            <div className="voice-selection-card__list-item-icon">
                                                                <MicOutlinedIcon />
                                                            </div>
                                                            <div className="voice-selection-card__list-item-main">
                                                                <h6 className="voice-selection-card__list-item-title">
                                                                    {voice.name || 'Voice'} <Badge variant="primary" className="badge-soft-secondary">{voice.gender}</Badge>
                                                                </h6>
                                                                <div
                                                                    className="voice-selection-card__list-item-preview"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onKeyDown={(e) => e.stopPropagation()}
                                                                    role="presentation"
                                                                >
                                                                    {voice.previewUrl || voice.audioUrl ? (
                                                                        <MiniVoicePreview
                                                                            src={voice.previewUrl || voice.audioUrl}
                                                                            playerId={id}
                                                                            activePlayerId={this.state.listPreviewPlayerId}
                                                                            setActivePlayerId={(pid) =>
                                                                                this.setState({ listPreviewPlayerId: pid })
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <p className="voice-selection-card__list-item-desc">
                                                                            {voice.description || voice.gender || ''}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {isActive ? (
                                                            <div
                                                                className="voice-selection-card__list-item-actions"
                                                                onClick={(e) => e.stopPropagation()}
                                                                onKeyDown={(e) => e.stopPropagation()}
                                                                role="presentation"
                                                            >
                                                                <Button
                                                                    type="button"
                                                                    variant="primary"
                                                                    className="voice-selection-btn-select voice-selection-btn-select--in-row"
                                                                    onClick={this.handleSelectVoice}
                                                                >
                                                                    {this.state.selectedVoice ? 'Change Voice' : 'Select Voice'}
                                                                </Button>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="voice-selection-btn-add-new voice-selection-btn-add-new--outside"
                                    onClick={this.handleAddNewVoice}
                                >
                                    Add New Voice
                                </Button>
                            </div>
                        </div>
                    ) : null}

                </div>

                {canUseDeepfake ? <SelectVoiceModal
                    show={this.state.showSelectVoiceModal}
                    handleClose={this.handleCloseSelectVoiceModal}
                    onSelectVoice={this.handleVoiceChosenFromModal}
                    vishingVoiceSelectionId={this.props.vishingVoiceSelectionId}
                    campaignTemplateId={this.props.campaignTemplateId}
                    onVishingVoiceSelectionCreated={this.props.onVishingVoiceSelectionCreated}
                    currentVoiceId={this.state.selectedVoice?.voiceId ?? this.state.selectedVoice?.id}
                /> : null}

                {canUseDeepfake ? <AddNewVoiceModal
                    show={this.state.showAddNewVoiceModal}
                    handleClose={this.handleCloseAddNewVoiceModal}
                    refreshVoices={this.loadSelectedVoices}
                /> : null}

                <div className="mt-2">
                    <div className='footer-wrapper justify-content-end d-flex'>
                        <Button variant="secondary" className="btn closeBtn mr-2" onClick={e => this.props.handleTabChange('prev')}><ArrowBackOutlinedIcon className="btn-icon"></ArrowBackOutlinedIcon> Previous</Button>
                        <Button variant="success" disabled={this.state.loading} type="button" onClick={this.handleCreateAiVishingPrompt}><Spinner loading={this.state.loading}></Spinner><ArrowForwardOutlinedIcon className={this.state.loading ? "btn-icon d-none" : "btn-icon"}></ArrowForwardOutlinedIcon> Go To Next</Button>
                    </div>
                </div>
            </>
        )
    }
}
